// Studio films in the CRM (B6, plan 2026-09-26 §3.3).
//
// A film made in the agency portal (the Studio) runs under a phantom pipeline
// user `studio-<echo_id>` (a re-cut inherits its parent's), so its dashboard
// rows used to show a meaningless contact and "Organic / shared". The bot now
// stamps the film's facts on its session and the dashboard indexer copies them
// onto the SESSION/ECHO rows: `studio_agency_id`, `studio_created_by` (portal
// user id), `studio_created_by_staff`, `studio_listing_group`,
// `studio_revision_index`, `studio_parent_echo_id`, `studio_format`.
//
// Creators travel as portal user ids, never e-mails (plan G10): the admin-only
// CRM resolves them from Supabase `agency_portal_users`. Pure helpers only, so
// they run in node-mode vitest.

import type {
  Agency,
  AgencyPortalUser,
  EchoflicksVideoSession,
} from "../types";

/** Pipeline user id prefix of every Studio film (bot delivery_sink.py). */
export const STUDIO_USER_PREFIX = "studio-";

export const isStudioUserId = (value: unknown): boolean =>
  typeof value === "string" && value.startsWith(STUDIO_USER_PREFIX);

type StudioFilmIdentity = Pick<
  EchoflicksVideoSession,
  "studio_agency_id" | "studio_echo_id" | "whatsapp_user_id"
> & { contact_id?: unknown };

const nonEmpty = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/**
 * True for a film made in the Studio: it carries the Studio facts, or (films
 * the backfill has not reached yet) its user is a `studio-` phantom. A
 * WhatsApp Echo — agency members' included — is never a Studio film.
 */
export const isStudioFilm = (
  echo: Partial<StudioFilmIdentity> | null | undefined,
): boolean =>
  Boolean(
    echo &&
      (nonEmpty(echo.studio_agency_id) ||
        nonEmpty(echo.studio_echo_id) ||
        isStudioUserId(echo.whatsapp_user_id) ||
        isStudioUserId(echo.contact_id)),
  );

export const STUDIO_FORMAT_CHOICES = [
  { id: "tour", name: "Tour" },
  { id: "eye_candy", name: "Eye candy" },
] as const;

/** The "made by staff" filter values the dashboard API accepts. */
export const STUDIO_STAFF_CHOICES = [
  { id: "true", name: "Made by staff" },
  { id: "false", name: "Made by the client" },
] as const;

/** "eye_candy" → "Eye candy"; an unknown format is humanised, never hidden. */
export const studioFormatLabel = (
  format: string | null | undefined,
): string | null => {
  const value = nonEmpty(format);
  if (!value) return null;
  const known = STUDIO_FORMAT_CHOICES.find((choice) => choice.id === value);
  if (known) return known.name;
  const spaced = value.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export type AgencyLookup = ReadonlyMap<string, Pick<Agency, "name">>;
export type LoginLookup = ReadonlyMap<
  string,
  Pick<AgencyPortalUser, "email" | "is_staff">
>;

/** The agency's name when the admin-only directory knows it, else null. */
export const studioAgencyName = (
  echo: Pick<EchoflicksVideoSession, "studio_agency_id">,
  agencies: AgencyLookup | null | undefined,
): string | null => {
  const id = nonEmpty(echo.studio_agency_id);
  if (!id) return null;
  return nonEmpty(agencies?.get(id)?.name) ?? null;
};

/** What stands in for the phantom contact: the agency, else "Studio film". */
export const studioAgencyLabel = (
  echo: Pick<EchoflicksVideoSession, "studio_agency_id">,
  agencies: AgencyLookup | null | undefined,
): string => studioAgencyName(echo, agencies) ?? "Studio film";

export type StudioCreator = {
  /** Portal user id, or null when the film predates the facts. */
  id: string | null;
  /** Resolved from Supabase by the admin-only CRM; null when unknown. */
  email: string | null;
  /** The film's frozen flag first, else the login's current flag. */
  isStaff: boolean | null;
};

export const studioCreator = (
  echo: Pick<
    EchoflicksVideoSession,
    "studio_created_by" | "studio_created_by_staff"
  >,
  logins: LoginLookup | null | undefined,
): StudioCreator => {
  const id = nonEmpty(echo.studio_created_by);
  const login = id ? logins?.get(id) : undefined;
  const frozen = echo.studio_created_by_staff;
  return {
    id,
    email: nonEmpty(login?.email) ?? null,
    isStaff:
      typeof frozen === "boolean"
        ? frozen
        : typeof login?.is_staff === "boolean"
          ? login.is_staff
          : null,
  };
};

/** The creator's e-mail, else a short, stable stand-in for the id. */
export const studioCreatorLabel = (creator: StudioCreator): string =>
  creator.email ??
  (creator.id ? `Portal user ${creator.id.slice(0, 8)}` : "Unknown creator");

export type StudioFilmOutcome =
  | "delivered"
  | "failed"
  | "cancelled"
  | "in_progress";

/** Delivered / failed / cancelled / still running, from the session status. */
export const studioFilmOutcome = (
  film: Pick<EchoflicksVideoSession, "status" | "has_final_video">,
): StudioFilmOutcome => {
  if (film.status === "completed" || film.has_final_video === true) {
    return "delivered";
  }
  if (film.status === "failed") return "failed";
  if (film.status === "cancelled") return "cancelled";
  return "in_progress";
};

/** A re-cut is any revision after the original cut (revision index 0). */
export const isStudioRecut = (
  film: Pick<
    EchoflicksVideoSession,
    "studio_revision_index" | "studio_parent_echo_id"
  >,
): boolean =>
  (typeof film.studio_revision_index === "number" &&
    film.studio_revision_index > 0) ||
  nonEmpty(film.studio_parent_echo_id) != null;

/** A 1–5 score (an agent's rating), or null for anything else. */
export const filmRating = (value: unknown): number | null =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= 1 &&
  value <= 5
    ? value
    : null;

export type CreatorFilmSummary = StudioCreator & {
  label: string;
  films: number;
  delivered: number;
  failed: number;
  cancelled: number;
  inProgress: number;
  recuts: number;
  formats: { format: string; label: string; count: number }[];
  lastFilmAt: string | null;
  /** Agent ratings (plan 2026-10-10): rated films and their mean score, one decimal. */
  rated: number;
  averageRating: number | null;
};

type StudioFilmRow = Pick<
  EchoflicksVideoSession,
  | "status"
  | "has_final_video"
  | "studio_created_by"
  | "studio_created_by_staff"
  | "studio_revision_index"
  | "studio_parent_echo_id"
  | "studio_format"
  | "display_date"
  | "completed_at"
  | "started_at"
  | "rating"
>;

const filmAt = (film: StudioFilmRow): string | null =>
  film.display_date ?? film.completed_at ?? film.started_at ?? null;

/** "Eye candy · Re-cut 2": the Studio film's format and revision, or null. */
export const studioFilmDetail = (
  film: Pick<
    EchoflicksVideoSession,
    "studio_format" | "studio_revision_index" | "studio_parent_echo_id"
  >,
): string | null => {
  const parts = [
    studioFormatLabel(film.studio_format),
    isStudioRecut(film)
      ? `Re-cut${film.studio_revision_index ? ` ${film.studio_revision_index}` : ""}`
      : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

/**
 * "Films by creator" for one agency (B6.3): per portal login, how many films,
 * how they ended, how many were re-cuts and in which formats — what Jonny
 * needs on a client call ("I see everything they've done, and they've had to
 * recut every one of them"). Most films first, then the most recent.
 */
export function summarizeFilmsByCreator(
  films: readonly StudioFilmRow[],
  logins: LoginLookup | null | undefined,
): CreatorFilmSummary[] {
  const byCreator = new Map<string, CreatorFilmSummary>();
  const formatCounts = new Map<string, Map<string, number>>();
  const ratingSums = new Map<string, number>();
  for (const film of films) {
    const creator = studioCreator(film, logins);
    const key = creator.id ?? "";
    let summary = byCreator.get(key);
    if (!summary) {
      summary = {
        ...creator,
        label: studioCreatorLabel(creator),
        films: 0,
        delivered: 0,
        failed: 0,
        cancelled: 0,
        inProgress: 0,
        recuts: 0,
        formats: [],
        lastFilmAt: null,
        rated: 0,
        averageRating: null,
      };
      byCreator.set(key, summary);
      formatCounts.set(key, new Map());
    }
    if (summary.isStaff == null && creator.isStaff != null) {
      summary.isStaff = creator.isStaff;
    }
    summary.films += 1;
    const outcome = studioFilmOutcome(film);
    if (outcome === "delivered") summary.delivered += 1;
    else if (outcome === "failed") summary.failed += 1;
    else if (outcome === "cancelled") summary.cancelled += 1;
    else summary.inProgress += 1;
    if (isStudioRecut(film)) summary.recuts += 1;
    const format = nonEmpty(film.studio_format);
    if (format) {
      const counts = formatCounts.get(key)!;
      counts.set(format, (counts.get(format) ?? 0) + 1);
    }
    const at = filmAt(film);
    if (at && (!summary.lastFilmAt || at > summary.lastFilmAt)) {
      summary.lastFilmAt = at;
    }
    const rating = filmRating(film.rating);
    if (rating != null) {
      summary.rated += 1;
      ratingSums.set(key, (ratingSums.get(key) ?? 0) + rating);
    }
  }
  for (const [key, summary] of byCreator) {
    if (summary.rated) {
      summary.averageRating =
        Math.round(((ratingSums.get(key) ?? 0) / summary.rated) * 10) / 10;
    }
    summary.formats = Array.from(formatCounts.get(key) ?? [])
      .map(([format, count]) => ({
        format,
        label: studioFormatLabel(format) ?? format,
        count,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }
  return Array.from(byCreator.values()).sort(
    (a, b) =>
      b.films - a.films ||
      String(b.lastFilmAt ?? "").localeCompare(String(a.lastFilmAt ?? "")) ||
      a.label.localeCompare(b.label),
  );
}

/** The Echo/session list URL filtered to one agency (and one creator). */
export const studioFilmsListPath = (
  resource: "echoflicks_echoes" | "echoflicks_video_sessions",
  agencyId: string,
  creatorId?: string | null,
): string =>
  `/${resource}?filter=${encodeURIComponent(
    JSON.stringify({
      studio_agency_id: agencyId,
      ...(creatorId ? { studio_created_by: creatorId } : {}),
    }),
  )}`;
